import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../Button'
import { strings } from '@/strings'

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title: ReactNode
    children: React.ReactNode
    confirmLabel?: string
    cancelLabel?: string
    onConfirm?: () => void
    isDestructive?: boolean
    isLoading?: boolean
    showFooter?: boolean
}

export function Modal({
    isOpen,
    onClose,
    title,
    children,
    confirmLabel = strings.common.confirm,
    cancelLabel = strings.common.cancel,
    onConfirm,
    isDestructive = false,
    isLoading = false,
    showFooter = true,
}: ModalProps) {
    const overlayRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isLoading) onClose()
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, onClose, isLoading])

    if (!isOpen) return null

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                ref={overlayRef}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={() => !isLoading && onClose()}
            />

            {/* Modal Content */}
            <div className="relative bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
                <div className="p-6 flex flex-col overflow-hidden">
                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                        {title}
                    </h3>
                    <div className="text-text-secondary-light dark:text-text-secondary-dark mb-6 overflow-y-auto flex-1">
                        {children}
                    </div>

                    {showFooter && (
                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={onClose}
                                disabled={isLoading}
                                className="!w-auto px-6"
                            >
                                {cancelLabel}
                            </Button>
                            {onConfirm && isDestructive && (
                                <Button
                                    onClick={onConfirm}
                                    isLoading={isLoading}
                                    variant="secondary"
                                    className="!w-auto px-6 !text-red-600 dark:!text-red-400 hover:!bg-red-50 dark:hover:!bg-red-950/30"
                                >
                                    {confirmLabel}
                                </Button>
                            )}
                            {onConfirm && !isDestructive && (
                                <Button
                                    onClick={onConfirm}
                                    isLoading={isLoading}
                                    className="!w-auto px-6"
                                >
                                    {confirmLabel}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}
